import { Form } from "@inertiajs/react"
import { PackageCheck } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { transitionsMissionCollabPath } from "@/routes"
import type { BrandMissionCollabRow } from "@/types/missions"

import { ReturnToFields } from "./collab-transition-return"

/**
 * OKS-32 — Brand-side `Mark delivered` confirm dialog (UX Spec §2.7).
 *
 * Wraps Inertia `<Form>` (Crew rule). Hidden `event=mark_delivered`
 * input drives the controller dispatch.
 *
 * Default focus on the **primary** action — operator-confirming, low
 * blast radius (UX Spec §6 #9).
 *
 * Helper copy "Sendcloud will automate this in a future update" sets
 * brand expectations: this is a manual fallback that will be replaced
 * by OKS-86 (carrier webhook).
 */
interface MarkDeliveredConfirmDialogProps {
  row: BrandMissionCollabRow
  missionId: number
}

export function MarkDeliveredConfirmDialog({
  row,
  missionId,
}: MarkDeliveredConfirmDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const ariaTitle = row.creator.handle
    ? `@${row.creator.handle}`
    : row.creator.name

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <Button
            type="button"
            variant="default"
            size="sm"
            aria-label={t("collabs.mark_delivered.aria", { title: ariaTitle })}
            data-testid="brand-mission-collab-mark-delivered-trigger"
          >
            <PackageCheck aria-hidden="true" className="mr-1 size-4" />
            {t("collabs.mark_delivered.cta")}
          </Button>
        }
      />
      <DialogContent
        data-testid="brand-mission-collab-mark-delivered-dialog"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>{t("collabs.mark_delivered.title")}</DialogTitle>
          <DialogDescription>
            {t("collabs.mark_delivered.body")}
          </DialogDescription>
        </DialogHeader>
        <Form
          action={transitionsMissionCollabPath(missionId, row.id)}
          method="post"
          options={{ preserveScroll: true }}
          onSuccess={() => setOpen(false)}
        >
          {({ processing }) => (
            <>
              <input type="hidden" name="event" value="mark_delivered" />
              {/* OKS-1038 — return_to/collabs_shown on the crew member page only. */}
              <ReturnToFields />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                  data-testid="brand-mission-collab-mark-delivered-cancel"
                >
                  {t("collabs.mark_delivered.cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  disabled={processing}
                  autoFocus
                  data-testid="brand-mission-collab-mark-delivered-confirm"
                >
                  {t("collabs.mark_delivered.confirm")}
                </Button>
              </DialogFooter>
            </>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  )
}
